import { useQuery } from "@tanstack/react-query";

// https://developers.google.com/maps/documentation/places/web-service/place-details
const URL = "https://places.googleapis.com/v1/places";

export type PlaceDetails = {
  displayName: {
    text: string;
    languageCode: string;
  };
  formattedAddress: string;
  location: {
    latitude: number;
    longitude: number;
  };
  addressComponents?: Array<{
    longText: string;
    shortText: string;
    types: string[];
    languageCode: string;
  }>;
};

export const fetchGooglePlaceDetails = async (placeId: string): Promise<PlaceDetails> => {
  const response = await fetch(`${URL}/${placeId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
      "X-Goog-FieldMask": "displayName,formattedAddress,location,addressComponents",
    },
  });

  if (!response.ok) {
    let errorMessage = `Error fetching place details: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData && errorData.error && errorData.error.message) {
        errorMessage = `Error fetching place details: ${errorData.error.message}`;
      }
    } catch {
      // Ignore JSON parse errors, use default errorMessage
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const useGetGooglePlaceDetails = (placeId: string | null) => {
  return useQuery<PlaceDetails>({
    queryKey: ["googlePlaceDetails", placeId],
    queryFn: () => fetchGooglePlaceDetails(placeId!),
    enabled: !!placeId,
  });
};
